import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { IoChevronDownOutline, IoChevronUpSharp } from "react-icons/io5";
import path from "path";

import { DashboardIcon, USerMgmtIcon, ContentIcon } from "../icons/sidebar";
import { PromotionIcon, TransactIcon, ReportIcon } from "../icons/sidebar";
import { PMIcon, AdminIcon, SettingsIcon, LogoutIcon } from "../icons/sidebar";

const sidebarData = [
  { title: "Dashboard", icon: DashboardIcon, path: "/dashboard" },
  { title: "User Management", icon: USerMgmtIcon, path: "/user-management" },
  {
    title: "Product Management",
    icon: PMIcon,
    path: "/product-management",
    subMenu: [
      {
        title: "Products",
        path: "/products",
        nestedSubMenu: [
          {
            title: "Data",
            path: "/products/data",
            nestedSubMenu: [
              { title: "Providers", path: "/products/data/providers" },
              { title: "Categories", path: "/products/data/categories" },
              { title: "Plans", path: "/products/data/plans" },
            ],
          },
          {
            title: "Airtime",
            path: "/products/airtime",
            nestedSubMenu: [
              { title: "Providers", path: "/products/airtime/providers" },
              { title: "Categories", path: "/products/airtime/categories" },
              { title: "Plans", path: "/products/airtime/plans" },
            ],
          },
          {
            title: "Electricity",
            path: "/products/electricity",
            nestedSubMenu: [
              { title: "Providers", path: "/products/electric/providers" },
              { title: "Categories", path: "/products/electric/categories" },
              { title: "Plans", path: "/products/electric/plans" },
            ],
          },
          {
            title: "Cable",
            path: "/products/cable",
            nestedSubMenu: [
              { title: "Providers", path: "/products/cable/providers" },
              { title: "Categories", path: "/products/cable/categories" },
              { title: "Plans", path: "/products/cable/plans" },
            ],
          },
          {
            title: "Gift Cards",
            path: "/products/giftcards",
            nestedSubMenu: [
              { title: "Providers", path: "/products/giftcards/providers" },
              { title: "Categories", path: "/products/giftcards/categories" },
              { title: "Plans", path: "/products/giftcards/plans" },
            ],
          },
          {
            title: "DG Coins",
            path: "/products/dgcoins",
            nestedSubMenu: [
              { title: "Providers", path: "/products/dgcoins/providers" },
              { title: "Categories", path: "/products/dgcoins/categories" },
              { title: "Plans", path: "/products/dgcoins/plans" },
            ],
          },

          {
            title: "Game Cards",
            path: "/products/gamecards",
            nestedSubMenu: [
              { title: "Providers", path: "/products/gamecards/providers" },
              { title: "Categories", path: "/products/gamecards/categories" },
              { title: "Plans", path: "/products/gamecards/plans" },
            ],
          },

          {
            title: "Top Up Cards",
            path: "/products/topupcards",
            nestedSubMenu: [
              { title: "Providers", path: "/products/topupcards/providers" },
              { title: "Categories", path: "/products/topupcards/categories" },
              { title: "Plans", path: "/products/topupcard/plans" },
            ],
          },
        ],
      },
    ],
  },
  { title: "Currencies", icon: PromotionIcon, path: "/currencies" },
  { title: "Countries", icon: PromotionIcon, path: "/countries" },
  { title: "Promotions", icon: PromotionIcon, path: "/promotions" },
  {
    title: "Transaction Management",
    icon: TransactIcon,
    path: "/transactions",
  },
  { title: "Reports & Analytics", icon: ReportIcon, path: "/reports" },
  { title: "Content Management", icon: ContentIcon, path: "/content" },
  { title: "Sliders Management", icon: ContentIcon, path: "/sliders" },
  { title: "Admin Management", icon: AdminIcon, path: "/admin-management" },
  { title: "Settings", icon: SettingsIcon, path: "/settings" },
  { title: "Logout", icon: LogoutIcon, path: "/" },
];

const Sidebar = () => {
  const location = useLocation();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [openNestedSubMenu, setOpenNestedSubMenu] = useState<string | null>(
    null
  );
  const [openThirdLevelSubMenu, setOpenThirdLevelSubMenu] = useState<
    string | null
  >(null);

  // Helper to check if a path is in a nested structure
  const findMenuPath = (
    data: any[],
    pathname: string,
    levels: string[] = []
  ): string[] | null => {
    for (const item of data) {
      if (item.path === pathname) {
        return [...levels, item.title];
      }
      if (item.subMenu) {
        const found = findMenuPath(item.subMenu, pathname, [
          ...levels,
          item.title,
        ]);
        if (found) return found;
      }
      if (item.nestedSubMenu) {
        const found = findMenuPath(item.nestedSubMenu, pathname, [
          ...levels,
          item.title,
        ]);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    // Find the menu path for the current location
    const menuPath = findMenuPath(sidebarData, location.pathname);

    // Set open states based on the menu path
    if (menuPath) {
      setOpenSubMenu(menuPath[0] || null);
      setOpenNestedSubMenu(menuPath[1] || null);
      setOpenThirdLevelSubMenu(menuPath[2] || null);
    }
  }, [location.pathname]);

  const toggleSubMenu = (title: string) => {
    setOpenSubMenu(openSubMenu === title ? null : title);
    if (openSubMenu !== title) {
      setOpenNestedSubMenu(null);
      setOpenThirdLevelSubMenu(null);
    }
  };

  const toggleNestedSubMenu = (title: string) => {
    setOpenNestedSubMenu(openNestedSubMenu === title ? null : title);
    if (openNestedSubMenu !== title) {
      setOpenThirdLevelSubMenu(null);
    }
  };

  const toggleThirdLevelSubMenu = (title: string) => {
    setOpenThirdLevelSubMenu(openThirdLevelSubMenu === title ? null : title);
  };

  return (
    <div className="h-fit min-w-[273px] col-2 bg-white dark:bg-BG-dark rounded-lg py-6 ml-10 px-2">
      {sidebarData.map((item, index) => {
        const isActive = location.pathname === item.path;
        const hasSubMenu = item.subMenu && item.subMenu.length > 0;
        const isSubMenuOpen = openSubMenu === item.title;

        // Check if any submenu item is active
        const isSubMenuItemActive =
          hasSubMenu &&
          item.subMenu.some(
            (subItem: any) => location.pathname === subItem.path
          );

        return (
          <div key={index} className="mb-1 ">
            {hasSubMenu ? (
              <>
                <button
                  className={`flex justify-between items-center hover:bg-pry-25 w-full py-2 px-4 rounded-lg transition ${
                    (isActive || isSubMenuItemActive || isSubMenuOpen) &&
                    "bg-pry-25"
                  }`}
                  onClick={() => toggleSubMenu(item.title)}
                >
                  <div className="flex items-center">
                    <div className="mr-2">
                      {item.icon({
                        active:
                          isActive || isSubMenuItemActive || isSubMenuOpen,
                      })}
                    </div>
                    <span
                      className={`font-satoshi font-inherit ${
                        isActive || isSubMenuItemActive || isSubMenuOpen
                          ? "text-pry-600"
                          : "text-gray-500"
                      }`}
                    >
                      {item.title}
                    </span>
                  </div>
                  <div>
                    {isSubMenuOpen ? (
                      <IoChevronUpSharp size={16} />
                    ) : (
                      <IoChevronDownOutline size={16} />
                    )}
                  </div>
                </button>

                {isSubMenuOpen && (
                  <div className="ml-8 mt-1">
                    {item.subMenu.map((subItem: any, subIndex: number) => {
                      const isSubItemActive =
                        location.pathname === subItem.path;
                      const hasNested =
                        subItem.nestedSubMenu &&
                        subItem.nestedSubMenu.length > 0;
                      const isNestedOpen = openNestedSubMenu === subItem.title;

                      // Check if any nested submenu item is active
                      const isNestedSubMenuItemActive =
                        hasNested &&
                        subItem.nestedSubMenu.some(
                          (nestedItem: any) =>
                            location.pathname === nestedItem.path
                        );

                      return (
                        <div key={subIndex}>
                          {hasNested ? (
                            <>
                              <button
                                onClick={() =>
                                  toggleNestedSubMenu(subItem.title)
                                }
                                className={`flex justify-between items-center w-full py-2 px-4 rounded-lg hover:bg-pry-25 ${
                                  isSubItemActive ||
                                  isNestedOpen ||
                                  isNestedSubMenuItemActive
                                    ? "bg-pry-25"
                                    : ""
                                }`}
                              >
                                <span
                                  className={`font-satoshi ${
                                    isSubItemActive ||
                                    isNestedOpen ||
                                    isNestedSubMenuItemActive
                                      ? "text-pry-600"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {subItem.title}
                                </span>
                                {isNestedOpen ? (
                                  <IoChevronUpSharp size={14} />
                                ) : (
                                  <IoChevronDownOutline size={14} />
                                )}
                              </button>

                              {isNestedOpen && (
                                <div className="ml-6">
                                  {subItem.nestedSubMenu.map(
                                    (nestedItem: any, nestedIndex: number) => {
                                      const isNestedActive =
                                        location.pathname === nestedItem.path;
                                      const hasThirdLevel =
                                        nestedItem.nestedSubMenu &&
                                        nestedItem.nestedSubMenu.length > 0;
                                      const isThirdLevelOpen =
                                        openThirdLevelSubMenu ===
                                        nestedItem.title;

                                      // Check if any third-level submenu item is active
                                      const isThirdLevelSubMenuItemActive =
                                        hasThirdLevel &&
                                        nestedItem.nestedSubMenu.some(
                                          (thirdItem: any) =>
                                            location.pathname === thirdItem.path
                                        );

                                      return (
                                        <div key={nestedIndex}>
                                          {hasThirdLevel ? (
                                            <>
                                              <button
                                                onClick={() =>
                                                  toggleThirdLevelSubMenu(
                                                    nestedItem.title
                                                  )
                                                }
                                                className={`flex justify-between items-center w-full py-2 px-4 rounded-lg hover:bg-pry-25 ${
                                                  isNestedActive ||
                                                  isThirdLevelOpen ||
                                                  isThirdLevelSubMenuItemActive
                                                    ? "bg-pry-25"
                                                    : ""
                                                }`}
                                              >
                                                <span
                                                  className={`font-satoshi ${
                                                    isNestedActive ||
                                                    isThirdLevelOpen ||
                                                    isThirdLevelSubMenuItemActive
                                                      ? "text-pry-600"
                                                      : "text-gray-500"
                                                  }`}
                                                >
                                                  {nestedItem.title}
                                                </span>
                                                {isThirdLevelOpen ? (
                                                  <IoChevronUpSharp size={13} />
                                                ) : (
                                                  <IoChevronDownOutline
                                                    size={13}
                                                  />
                                                )}
                                              </button>
                                              {isThirdLevelOpen && (
                                                <div className="ml-6">
                                                  {nestedItem.nestedSubMenu.map(
                                                    (
                                                      thirdItem: any,
                                                      thirdIndex: number
                                                    ) => {
                                                      const isThirdActive =
                                                        location.pathname ===
                                                        thirdItem.path;
                                                      return (
                                                        <Link
                                                          to={thirdItem.path}
                                                          key={thirdIndex}
                                                        >
                                                          <div
                                                            className={`py-2 px-4 rounded-lg hover:bg-pry-25 transition ${
                                                              isThirdActive
                                                                ? "bg-pry-25"
                                                                : ""
                                                            }`}
                                                          >
                                                            <span
                                                              className={`font-satoshi text-sm ${
                                                                isThirdActive
                                                                  ? "text-pry-600"
                                                                  : "text-gray-500"
                                                              }`}
                                                            >
                                                              {thirdItem.title}
                                                            </span>
                                                          </div>
                                                        </Link>
                                                      );
                                                    }
                                                  )}
                                                </div>
                                              )}
                                            </>
                                          ) : (
                                            <Link to={nestedItem.path}>
                                              <div
                                                className={`py-2 px-4 rounded-lg hover:bg-pry-25 transition ${
                                                  isNestedActive
                                                    ? "bg-pry-25"
                                                    : ""
                                                }`}
                                              >
                                                <span
                                                  className={`font-satoshi text-sm ${
                                                    isNestedActive
                                                      ? "text-pry-600"
                                                      : "text-gray-500"
                                                  }`}
                                                >
                                                  {nestedItem.title}
                                                </span>
                                              </div>
                                            </Link>
                                          )}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <Link to={subItem.path}>
                              <div
                                className={`flex items-center py-2 px-4 rounded-lg hover:bg-pry-25 transition ${
                                  isSubItemActive ? "bg-pry-25" : ""
                                }`}
                              >
                                <span
                                  className={`font-satoshi font-inherit ${
                                    isSubItemActive
                                      ? "text-pry-600"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {subItem.title}
                                </span>
                              </div>
                            </Link>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <Link to={item.path}>
                <button
                  className={`flex items-center hover:bg-pry-25 w-full py-2 px-4 rounded-lg transition ${
                    isActive && "bg-pry-25"
                  }`}
                >
                  <div className="mr-2">{item.icon({ active: isActive })}</div>
                  <span
                    className={`font-satoshi font-inherit ${
                      isActive ? "text-pry-600" : "text-gray-500"
                    }`}
                  >
                    {item.title}
                  </span>
                </button>
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Sidebar;
